import { getExecutedReportData, getExecutedReportGroups } from "@/Api/ReportApi";
import { GetExecutedReportGroupsInputSchema } from "@/Schemas/Reports/GetExecutedReportGroupsInput";
import { GetExecutedReportGroupsOutputSchema } from "@/Schemas/Reports/GetExecutedReportGroupsOutput";
import { GetMyReportsExecutionHistoryDto } from "@/Schemas/Reports/GetMyReportsExecutionHistoryDto";
import { Box, CircularProgress, Tab, Tabs, TextField, Typography, useTheme } from "@mui/material";
import { useEffect } from "react";
import { SnackbarProvider, SnackbarType, getCenterStyles, getFluidBoxStyles, useTask, useControlledState, Column, TextOverflow, capitalizeSentences, DataTable, Pagination, getFlexStyles } from "vig-ui-library";
import { DataNotFound } from "../DataNotFound";
import { GetExecutedReportDataInput, GetExecutedReportDataInputSchema } from "@/Schemas/Reports/GetExecutedReportDataInput";
import { UseControlledStateReturn } from "vig-ui-library/src/hooks/useControlledState";
import { GetExecutedReportDataOutput } from "@/Schemas/Reports/GetExecutedReportDataOutput";
import { ReportExecutionHistoryDataDto } from "@/Schemas/Reports/ReportExecutionHistoryDataDto";
import { ParameterListValueTypeEnum } from "@/Enums/ParameterListValueType";

export type AssemblyDataProps = {
    executedReport: GetMyReportsExecutionHistoryDto;
}

type RowData = ReportExecutionHistoryDataDto & { data: Record<string, any>[] }

export const AssemblyData = ({ executedReport }: AssemblyDataProps) => {
    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const theme = useTheme();

    const [query, setQuery, setQueryAndRerender, setQueryDebounced]: UseControlledStateReturn<GetExecutedReportDataInput> = useControlledState(GetExecutedReportDataInputSchema.parse({
        group: "",
        reportHistoryId: executedReport.id,
        limit: 10,
        page: 1,
    }));

    const [tableDefinition, setTableDefinition] = useControlledState<GetExecutedReportDataOutput | null>(null);

    const dataTaskAbortContorller = new AbortController();

    const getDataTask = useTask(async () => {
        try {
            const response = await getExecutedReportData(query.current, { signal: dataTaskAbortContorller.signal });
            setTableDefinition(response);

            const rawItems = response?.reportExecutionHistory?.reportExecutionHistoryData || [];

            const items: RowData[] = rawItems.map((item) => {
                return {
                    ...item,
                    data: item.jsonData ? JSON.parse(item.jsonData) : {},
                }
            });

            return {
                items,
                count: response?.count || 0,
            }
        } catch (err) {
            openSnackbar({
                text: "Nepodarilo sa načítať dáta, skúste to znova.",
                type: SnackbarType.ERROR,
            });
            return { items: [], count: 0 }
        }
    });

    const { value: groupsOutput, perform: getGroups, isRunning: groupsTaskRunning } = useTask(async (signal: AbortSignal) => {
        try {
            const input = GetExecutedReportGroupsInputSchema.parse({
                reportHistoryId: executedReport.id
            });
            const response = await getExecutedReportGroups(input, { signal })
            if (response.groups.length) {
                setQuery({ ...query.current, group: response.groups[0] });
                await getDataTask.perform();
            }
            return response;
        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            openSnackbar({
                text: "Nepodarilo sa načítať dáta, skúste to znova.",
                type: SnackbarType.ERROR,
            });
            return GetExecutedReportGroupsOutputSchema.parse({ groups: [] });
        }
    });

    useEffect(() => {
        const abortController = new AbortController();
        const signal: AbortSignal = abortController.signal;
        getGroups(signal);
        return () => {
            abortController.abort();
        };
    }, []);

    const columnsData = tableDefinition.current?.reportExecutionHistory?.reportExecutionHistoryColumns || [];

    const columns: Column<RowData>[] = columnsData.map((columnData) => {
        return {
            width: 160,
            name: capitalizeSentences(columnData.column) || "Neznámy stĺpec",
            tableCellBuilder: (row: RowData) => {
                type DataObject = { value?: string | number, type?: ParameterListValueTypeEnum };

                const data = row.data[0] || {};

                const dataObject: DataObject = data[columnData.column] || {};

                let value = dataObject.value;

                if (!value) value = "";

                if (typeof value === "number") value = value.toString();

                return <TextOverflow
                    text={value}
                    width="160px"
                />
            }
        }
    });

    return (
        <Box sx={{ ...getFlexStyles("column", true), height: "100%", overflow: "hidden" }}>
            {!groupsTaskRunning && !groupsOutput?.groups.length && (
                <DataNotFound
                    title="Žiadne dáta"
                    description="Pre tento záznam nie sú k dispozícii žiadne dáta."
                />
            )}

            {groupsTaskRunning && (
                <Box sx={{ ...getFluidBoxStyles(), ...getCenterStyles() }}>
                    <CircularProgress />
                </Box>
            )}

            {!!groupsOutput?.groups.length && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
                    <Tabs
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        variant="scrollable"
                        value={query.current.group}
                        onChange={(_event, newValue: string) => {
                            setQueryAndRerender({ ...query.current, group: newValue });
                        }}
                    >
                        {groupsOutput.groups.map((group, index) => (
                            <Tab
                                key={index}
                                label={<Typography
                                    variant="subtitle2"
                                    fontSize={15}
                                    color={query.current.group === group ? theme.palette.primary.main : theme.palette.text.secondary}
                                >{group}</Typography>}
                                value={group}
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            {!groupsTaskRunning && !!groupsOutput?.groups.length && (
                <>
                    <Box px={2}>
                        <TextField
                            sx={{ marginBottom: 1 }}
                            label="Hľadať v dátach"
                            placeholder="Zadajte hľadaný výraz"
                            variant="filled"
                            size="small"
                            onChange={(event) => {
                                const { value } = event.target;
                                setQueryDebounced({ ...query.current, dataFilter: value });
                            }}
                            fullWidth
                        />
                    </Box>

                    {!!getDataTask?.value?.count && (
                        <Pagination
                            pageSizeOptions={[10, 25]}
                            totalCount={getDataTask?.value?.count || 0}
                            page={query.current.page || 1}
                            pageSize={query.current.limit || 10}
                            onChange={(page: number, limit: number) => {
                                setQueryAndRerender({ ...query.current, page, limit });
                            }}
                        />
                    )}

                    <DataTable
                        columns={columns}
                        getDataTask={getDataTask}
                        tableMinWidth={columns.length * 160}
                        query={query.current}
                        setQuery={setQueryAndRerender}
                        runOnlyWhenQueryChanges={true}
                        abortController={dataTaskAbortContorller}
                    />
                </>
            )}
        </Box>
    );
}